// Pure helpers for the กระทบยอด สปสช / ประกันสังคม page (unit-tested in tests/unit/reconciliation.test.mjs).

const THAI_SHORT_MONTHS = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

/**
 * Thai national ID check: 13 digits, the last being (11 - Σ d[i]·(13-i) mod 11) mod 10.
 * Catches most single-digit OCR misreads before they turn into silently unmatched lines.
 */
export function isValidThaiCid(cid) {
  const s = String(cid ?? "");
  if (!/^\d{13}$/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(s[i]) * (13 - i);
  return (11 - (sum % 11)) % 10 === Number(s[12]);
}

/** null when the rows add up to the document total (or there is no total); otherwise both sums. */
export function linesTotalMismatch(lines, depositAmount) {
  if (depositAmount === null || depositAmount === undefined || depositAmount === "") return null;
  const toSatang = (v) => Math.round(Number(v || 0) * 100);
  const linesSatang = (lines || []).reduce((sum, l) => sum + toSatang(l.amount), 0);
  if (linesSatang === toSatang(depositAmount)) return null;
  return { linesTotal: linesSatang / 100, depositAmount: Number(depositAmount) };
}

/** "2 ต.ค. 69" — Buddhist-era short year (moment's th locale keeps the CE year). */
export function formatThaiShortDate(date, { withTime = false } = {}) {
  if (!date) return "";
  const dateOnly = typeof date === "string" && date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  // Parse a bare YYYY-MM-DD as a local calendar day so it never shifts across timezones.
  const d = dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const text = `${d.getDate()} ${THAI_SHORT_MONTHS[d.getMonth()]} ${String(d.getFullYear() + 543).slice(-2)}`;
  if (!withTime) return text;
  const pad = (n) => String(n).padStart(2, "0");
  return `${text} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The backend's error text ({errorCode, errorMessage: string}), never its generic 5xx text. */
export function apiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  const status = err?.response?.status ?? data?.errorCode;
  if (status >= 500) return fallback;
  if (typeof data?.errorMessage === "string" && data.errorMessage) return data.errorMessage;
  return data?.errorMessage?.message || data?.message || fallback;
}

/**
 * Undo reverts a whole batch (one statement), not just the clicked row, so the confirm text
 * must say so and name everyone it will send back to ค้างรับ.
 */
export function undoConfirmMessage(item, paidRows) {
  const batchRows = (paidRows || []).filter((r) => r.batchId === item.batchId);
  if (batchRows.length <= 1) return `ต้องการยกเลิกการรับเงินของ "${item.patientName}" ?`;
  const SHOWN = 5;
  const names = batchRows.slice(0, SHOWN).map((r) => r.patientName).join(", ");
  const more = batchRows.length > SHOWN ? ` และอีก ${batchRows.length - SHOWN} รายการ` : "";
  return `การยกเลิกจะย้อนทั้งเอกสาร "${item.statementRef}" จำนวน ${batchRows.length} รายการ: ${names}${more} — ต้องการยกเลิกทั้งหมด ?`;
}

/**
 * Unmatching one paid row: its claim goes back to ค้างรับ and, when it came from a statement, that
 * statement line goes to รอตรวจสอบ to be matched again. A mark-paid row has no statement line.
 */
export function unmatchConfirmMessage(row) {
  if (row.batchSource === "MANUAL") return `ยกเลิกการรับเงินของ "${row.patientName}" ? รายการนี้จะกลับไปที่ค้างรับ`;
  return `ยกเลิกการจับคู่ของ "${row.patientName}" ? รายการนี้จะกลับไปที่ค้างรับ และบรรทัดในเอกสาร ${row.statementRef || ""} จะไปอยู่ที่รอตรวจสอบเพื่อจับคู่ใหม่ รายการอื่นในเอกสารไม่เปลี่ยน`;
}

const toIsoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** { dateFrom, dateTo } covering the last `months` months up to `today`, both inclusive. */
export function monthsBackRange(months, today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth() - months, 1);
  const lastDay = new Date(from.getFullYear(), from.getMonth() + 1, 0).getDate();
  from.setDate(Math.min(today.getDate(), lastDay));
  return { dateFrom: toIsoDay(from), dateTo: toIsoDay(today) };
}

const formatBaht = (n) =>
  Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "42 รายการ · รวม 37,800.00 บาท" over every row in the range, not just the loaded page. */
export function listSummaryText(pagination, summary, unit = "รายการ") {
  if (!pagination) return "";
  return `${Number(pagination.total || 0).toLocaleString("th-TH")} ${unit} · รวม ${formatBaht(summary?.totalAmount)} บาท`;
}

/** The list is capped server-side; say so instead of silently dropping rows. */
export function truncationNote(pagination) {
  if (!pagination || !(pagination.total > pagination.count)) return null;
  const n = (v) => Number(v).toLocaleString("th-TH");
  return `แสดง ${n(pagination.count)} จาก ${n(pagination.total)} รายการ กรุณาเลือกช่วงวันที่ให้แคบลงเพื่อดูรายการที่เหลือ`;
}

const itemIds = (csv) => String(csv || "").split(",").filter(Boolean).map(Number);

/**
 * NHSO tab filters carried over from the old "รายงาน สปสช." report: a doctor (exact name) and a set
 * of treatment ids (a visit matches when it includes any of them). Empty filters keep everything.
 */
export function filterNhsoVisits(rows, { doctor = null, txIds = [] } = {}) {
  return (rows || []).filter((r) => {
    if (doctor && r.doctorName !== doctor) return false;
    if (txIds.length && !itemIds(r.treatmentItemIds).some((id) => txIds.includes(id))) return false;
    return true;
  });
}

/** Distinct doctor names in the loaded visits, for the แพทย์ filter. */
export function doctorOptions(rows) {
  return [...new Set((rows || []).map((r) => r.doctorName).filter(Boolean))].sort((a, b) => a.localeCompare(b, "th"));
}

/**
 * Excel rows (header, one row per claim, total) for a reconciliation tab, built from the data
 * rather than the rendered table so checkboxes and undo buttons stay out of the file.
 * NHSO rows are visits and carry แพทย์ / รายการรักษา, as the old รายงาน สปสช. export did.
 */
export function claimsExportRows({ rows, payer, status }) {
  const nhso = payer === "NHSO";
  const paid = status === "paid";
  const cols = [
    ["วันที่ใช้สิทธิ", (r) => formatThaiShortDate(r.serviceDate)],
    ["เลขบัตร ปชช.", (r) => r.cid || ""],
    ["ชื่อ-สกุล", (r) => r.patientName || ""],
    ...(nhso ? [["แพทย์", (r) => r.doctorName || ""], ["รายการรักษา", (r) => r.treatmentItems || ""]] : []),
    ["เลขที่ใบเสร็จ", (r) => r.receiptNo || ""],
    [nhso ? "ยอดเบิก" : "จำนวนเงิน", (r) => Number(r.amount || 0)],
    ...(paid
      ? [["เลขที่คำสั่งจ่าย", (r) => r.statementRef || ""], ["วันที่รับ", (r) => formatThaiShortDate(r.reconciledAt, { withTime: true })]]
      : []),
  ];
  const list = rows || [];
  const amountAt = cols.findIndex(([label]) => label === "ยอดเบิก" || label === "จำนวนเงิน");
  const total = cols.map(() => "");
  total[0] = `รวม ${list.length} ${nhso ? "visit" : "รายการ"}`;
  total[amountAt] = list.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  return [cols.map(([label]) => label), ...list.map((r) => cols.map(([, value]) => value(r))), total];
}

const localDay = (date) => {
  if (!date) return null;
  const m = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** Whole days a claim has been outstanding (ค้างมา), counted from its service date. */
export function ageDays(serviceDate, today = new Date()) {
  const d = localDay(serviceDate);
  if (!d) return null;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((t - d) / 86400000);
}

/**
 * รับแล้ว "ตามเอกสาร": one group per statement (batch), newest first. `totalMatches` compares the
 * statement's own total with what was matched; null when the statement had no total.
 */
export function groupPaidByStatement(rows) {
  const groups = new Map();
  for (const r of rows || []) {
    if (!groups.has(r.batchId)) {
      groups.set(r.batchId, {
        batchId: r.batchId, statementRef: r.statementRef, statementDate: r.statementDate,
        batchSource: r.batchSource, depositAmount: r.depositAmount, reconciledAt: r.reconciledAt, rows: [],
      });
    }
    const g = groups.get(r.batchId);
    g.rows.push(r);
    if (r.reconciledAt > g.reconciledAt) g.reconciledAt = r.reconciledAt;
  }
  return [...groups.values()]
    .map((g) => {
      const matchedSatang = g.rows.reduce((sum, r) => sum + Math.round(Number(r.amount || 0) * 100), 0);
      const hasDeposit = g.depositAmount !== null && g.depositAmount !== undefined && g.depositAmount !== "";
      return {
        ...g,
        matchedAmount: matchedSatang / 100,
        totalMatches: hasDeposit ? Math.round(Number(g.depositAmount) * 100) === matchedSatang : null,
      };
    })
    .sort((a, b) => String(b.reconciledAt).localeCompare(String(a.reconciledAt)));
}

/**
 * Why a statement line did not match, and the claim to offer when the same patient has an
 * outstanding claim on another date (nearest date wins).
 */
export function reviewSuggestion(line, outstandingClaims) {
  const cid = String(line.cid || "").trim();
  const sameCid = (outstandingClaims || []).filter((c) => String(c.cid || "").trim() === cid);
  if (sameCid.length) {
    const target = localDay(line.serviceDate);
    const gap = (c) => Math.abs(localDay(c.serviceDate) - target);
    const claim = [...sameCid].sort((a, b) => gap(a) - gap(b))[0];
    return { kind: "date-mismatch", claim };
  }
  if (/^\d{13}$/.test(cid) && !isValidThaiCid(cid)) return { kind: "cid-misread", claim: null };
  return { kind: "cid-not-found", claim: null };
}

/** The ช่วงด่วน chips: เดือนนี้ / เดือนที่แล้ว / ย้อนหลัง 60 วัน / ทั้งหมด. */
export function quickRange(key, today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (key) {
    case "thisMonth":
      return { dateFrom: toIsoDay(new Date(y, m, 1)), dateTo: toIsoDay(today) };
    case "lastMonth":
      return { dateFrom: toIsoDay(new Date(y, m - 1, 1)), dateTo: toIsoDay(new Date(y, m, 0)) };
    case "last60":
      return { dateFrom: toIsoDay(new Date(y, m, today.getDate() - 60)), dateTo: toIsoDay(today) };
    default:
      return { dateFrom: "", dateTo: "" };
  }
}

const CLAIM_PAYER = { SSO: "ปกส.", NHSO: "สปสช." };

/**
 * สถานะเบิก cell of the receipt report: one badge per claim on the receipt. `tone` picks the badge
 * colour; an unpaid claim served before reconciliation went live is not owed ("before_start").
 */
export function claimBadges(claims, today = new Date()) {
  return (claims || []).map((c) => {
    const paid = c.status === "paid";
    const old = c.status === "before_start";
    return {
      payer: CLAIM_PAYER[c.payer] || c.payer,
      status: paid ? "รับแล้ว" : old ? "ก่อนเริ่มใช้ระบบ" : `ค้างรับ ${ageDays(c.serviceDate, today)} วัน`,
      paid,
      doc: paid ? c.statementRef || "" : "",
      tone: paid ? "paid" : old ? "old" : "pending",
    };
  });
}

/** The ทั้งหมด chip names the day ค้างรับ counts from (the backend's go-live date). */
export function allRangeLabel(startDate) {
  return startDate ? `ทั้งหมด (ตั้งแต่ ${formatThaiShortDate(startDate)})` : "ทั้งหมด";
}

/** Served before reconciliation went live: listed only on request, and not counted as owed. */
export function isBeforeStart(serviceDate, startDate) {
  return !!startDate && !!serviceDate && String(serviceDate).slice(0, 10) < startDate;
}

const AGE_BOUNDS = { d0_30: [0, 30], d31_60: [31, 60], d61_90: [61, 90], d90p: [91, null], all: [0, null] };

/**
 * The service-date range that holds one age bucket as of `asOf`, so the aging report can open the
 * reconciliation page filtered to the same claims (a claim aged N days was served on asOf − N).
 */
export function bucketServiceRange(bucket, asOf) {
  const [minAge, maxAge] = AGE_BOUNDS[bucket] || AGE_BOUNDS.all;
  const base = localDay(asOf);
  const back = (days) => toIsoDay(new Date(base.getFullYear(), base.getMonth(), base.getDate() - days));
  return { dateFrom: maxAge === null ? "" : back(maxAge), dateTo: back(minAge) };
}

/**
 * The filter range stretched to cover `dates` (an import's service-date span), or null when it
 * already does — including ทั้งหมด (no range), which shows everything.
 */
export function widenRangeToInclude(range, dates) {
  const { dateFrom = "", dateTo = "" } = range || {};
  if (!dateFrom && !dateTo) return null;
  const known = (dates || []).filter(Boolean).sort();
  if (!known.length) return null;
  const from = dateFrom && known[0] < dateFrom ? known[0] : dateFrom;
  const to = dateTo && known[known.length - 1] > dateTo ? known[known.length - 1] : dateTo;
  return from === dateFrom && to === dateTo ? null : { dateFrom: from, dateTo: to };
}

const TITLE = /^(นางสาว|นาง|นาย|น\.ส\.|ด\.ช\.|ด\.ญ\.|เด็กชาย|เด็กหญิง|mrs?\.?|ms\.?|miss)\s*/i;
const nameTokens = (name) => String(name || "").trim().replace(TITLE, "").split(/\s+/).filter(Boolean);

const bigrams = (s) => {
  const out = [];
  for (let i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2));
  return out;
};
const diceSimilarity = (a, b) => {
  const x = bigrams(a);
  const y = bigrams(b);
  if (!x.length || !y.length) return 0;
  const pool = [...y];
  let hits = 0;
  for (const g of x) {
    const i = pool.indexOf(g);
    if (i >= 0) {
      hits++;
      pool.splice(i, 1);
    }
  }
  return (2 * hits) / (x.length + y.length);
};

// One digit off or two neighbours swapped: the usual OCR / typing slips on a 13-digit ID.
function cidSignal(a, b) {
  if (!a || !b) return null;
  if (a === b) return { level: "exact", text: "ตรง", score: 50 };
  if (a.length !== 13 || b.length !== 13) return null;
  const diff = [...a].map((ch, i) => (ch === b[i] ? -1 : i)).filter((i) => i >= 0);
  if (diff.length === 1) return { level: "near", text: "ต่าง 1 หลัก", score: 30 };
  if (diff.length === 2 && diff[1] === diff[0] + 1 && a[diff[0]] === b[diff[1]] && a[diff[1]] === b[diff[0]]) {
    return { level: "near", text: "สลับหลัก", score: 30 };
  }
  return null;
}

function nameSignal(a, b) {
  const x = nameTokens(a);
  const y = nameTokens(b);
  if (!x.length || !y.length) return null;
  if (x.join(" ") === y.join(" ")) return { level: "exact", text: "ตรง", score: 30 };
  if (x[0] === y[0]) return { level: "near", text: "ชื่อตรง", score: 15 };
  if (x.length > 1 && y.length > 1 && x[x.length - 1] === y[y.length - 1]) return { level: "near", text: "นามสกุลตรง", score: 12 };
  if (diceSimilarity(x.join(""), y.join("")) >= 0.6) return { level: "near", text: "ชื่อคล้าย", score: 10 };
  return null;
}

const NEAR_DAYS = 7;
function dateSignal(a, b) {
  const x = localDay(a);
  const y = localDay(b);
  if (!x || !y) return null;
  const days = Math.round(Math.abs(x - y) / 86400000);
  if (days === 0) return { level: "exact", text: "ตรง", score: 20 };
  if (days <= NEAR_DAYS) return { level: "near", text: `ห่าง ${days} วัน`, score: 20 - 2 * days };
  return null;
}

const SUGGEST_AT = 40; // e.g. same CID, or same name + same day

/**
 * Every outstanding claim scored against an unmatched statement line, best first, with the fields
 * that agree (`signals`: cid / name / date / amount, each exact or near) so staff can see why a
 * claim is offered. Amount is skipped for NHSO, where every visit is 700.
 */
export function rankClaimsForLine(line, claims, { payer } = {}) {
  const cid = String(line?.cid || "").trim();
  return (claims || [])
    .map((claim) => {
      const signals = [
        ["cid", cidSignal(cid, String(claim.cid || "").trim())],
        ["name", nameSignal(line?.patientName, claim.patientName)],
        ["date", dateSignal(line?.serviceDate, claim.serviceDate)],
        [
          "amount",
          payer !== "NHSO" && Number(line?.amount) > 0 && Number(line.amount) === Number(claim.amount)
            ? { level: "exact", text: "ตรง", score: 5 }
            : null,
        ],
      ]
        .filter(([, s]) => s)
        .map(([field, s]) => ({ field, ...s }));
      const score = signals.reduce((sum, s) => sum + s.score, 0);
      const target = localDay(line?.serviceDate);
      const gap = target && localDay(claim.serviceDate) ? Math.abs(localDay(claim.serviceDate) - target) : Infinity;
      return { claim, score, signals: signals.map(({ field, level, text }) => ({ field, level, text })), suggested: score >= SUGGEST_AT, gap };
    })
    .sort((a, b) => b.score - a.score || a.gap - b.gap)
    .map(({ gap, ...r }) => r); // eslint-disable-line no-unused-vars
}

/** How the OCR review shows the uploaded scan: "pdf" (browser viewer), "image", or null. */
export function scanPreviewKind(file) {
  if (!file) return null;
  const type = String(file.type || "");
  if (type === "application/pdf" || /\.pdf$/i.test(file.name || "")) return "pdf";
  if (type.startsWith("image/") || /\.(png|jpe?g)$/i.test(file.name || "")) return "image";
  return null;
}

/** The document states how many rows it has (รวมจำนวนราย); say so when OCR read a different number. */
export function rowCountMismatch(lines, declaredRowCount) {
  if (!declaredRowCount || (lines || []).length === declaredRowCount) return null;
  return `เอกสารระบุ ${declaredRowCount} ราย แต่ระบบอ่านได้ ${(lines || []).length} แถว กรุณาเทียบกับเอกสารแล้วเพิ่มหรือลบแถวให้ครบ`;
}

/**
 * Some ประกันสังคม documents are only the detail table, with no เลขที่คำสั่งจ่าย or ที่ รง. Name one by its
 * payer and date so the duplicate-import check still has a key; staff can change it in the review.
 */
export function fallbackStatementRef(statementRef, statementDate) {
  if (statementRef) return statementRef;
  return statementDate ? `ปกส. ${formatThaiShortDate(statementDate)}` : "";
}

/** The import dialog's line about this month's AI reads ({ used, limit } from the API; null limit = unlimited). */
export function ocrQuotaStatus(quota) {
  if (!quota) return null;
  if (quota.limit === null || quota.limit === undefined) {
    return { left: null, exhausted: false, text: "อ่านด้วย AI ได้ไม่จำกัด" };
  }
  const left = Math.max(0, quota.limit - quota.used);
  return left === 0
    ? { left, exhausted: true, text: `อ่านด้วย AI ครบ ${quota.limit} ครั้งของเดือนนี้แล้ว` }
    : { left, exhausted: false, text: `อ่านด้วย AI ได้อีก ${left} จาก ${quota.limit} ครั้งเดือนนี้` };
}

/** Undoing a statement from รอตรวจสอบ: it may have matched nothing, so it never shows under รับแล้ว. */
export function undoStatementConfirm(statementRef, reviewCount) {
  return `ยกเลิกเอกสาร ${statementRef} ทั้งฉบับ ? บรรทัดที่รอตรวจสอบ ${reviewCount} บรรทัดจะถูกลบ และรายการที่จับคู่ไปแล้วจากเอกสารนี้จะกลับไปที่ค้างรับ จากนั้นนำเข้าไฟล์นี้ใหม่ได้`;
}

/** The duplicate-import 409 says "undo the earlier batch first"; say where that button is. */
export function duplicateImportHint(message) {
  return /ถูกนำเข้าไปแล้ว/.test(message || "") ? `${message} (ปุ่ม "ยกเลิกทั้งเอกสาร" ในช่องรอตรวจสอบหรือรับแล้ว)` : message;
}
